import React, { useMemo } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { TrendingDown, TrendingUp, Check, Edit2, AlertTriangle, Repeat } from 'lucide-react';
import { Modal, ListRow, EmptyState } from '../Common/Common';
import { formatDate } from '../../utils/formatting';
import {
  getCategoryMonthlySeries, detectOutlierTransactions, detectRepetitiveExpenses, recommendCategoryBudget,
} from '../../utils/calculations';

const SectionLabel = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-tertiary)', margin: '18px 0 10px' }}>
    {children}
  </div>
);

function Stat({ label, value, cls = '' }) {
  return (
    <div style={{ padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: 8 }}>
      <div className="muted" style={{ fontSize: 11 }}>{label}</div>
      <div className={`mono ${cls}`} style={{ fontSize: 16, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

/**
 * Per-category analytics: 12-month %-of-budget graph, summary stats, outliers,
 * repetitive (likely recurring) expenses, and a right-size-the-budget recommendation.
 */
export default function CategoryDetailModal({
  category, transactions, budgetItem, currentBudget, fmt, settings, onApplyBudget, onEdit, onClose,
}) {
  const series = useMemo(
    () => getCategoryMonthlySeries(transactions, budgetItem, category.id, { months: 12 }),
    [transactions, budgetItem, category.id]
  );

  const windowTxns = useMemo(() => {
    const startMonth = series[0]?.month || '0000-00';
    return (transactions || []).filter(
      t => t.category === category.id && t.type === 'expense' && t.date.slice(0, 7) >= startMonth
    );
  }, [transactions, category.id, series]);

  const outliers = useMemo(() => detectOutlierTransactions(windowTxns), [windowTxns]);
  const repetitive = useMemo(() => detectRepetitiveExpenses(windowTxns), [windowTxns]);
  const rec = useMemo(() => recommendCategoryBudget(series, currentBudget), [series, currentBudget]);

  const total = windowTxns.reduce((s, t) => s + (t.amount || 0), 0);
  const txCount = windowTxns.length;
  const hasAnyBudget = series.some(m => m.budget > 0);

  return (
    <Modal title={`${category.name} — insights`} onClose={onClose} wide
      footer={<button className="btn" onClick={onClose}>Close</button>}>

      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }} className="cat-detail-stats">
        <Stat label="Monthly budget" value={currentBudget > 0 ? fmt(currentBudget) : '—'} />
        <Stat label="Avg / mo" value={fmt(rec.avg)} cls="expense-text" />
        <Stat label="Months over" value={`${rec.monthsOver}/12`} cls={rec.monthsOver > 0 ? 'expense-text' : ''} />
        <Stat label="12-mo total" value={fmt(total)} />
      </div>

      {/* Recommendation */}
      <SectionLabel>Recommendation</SectionLabel>
      <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
        {rec.action === 'ok' ? (
          <div className="flex" style={{ gap: 8, alignItems: 'center', fontSize: 13 }}>
            <Check size={16} style={{ color: 'var(--income)' }} />
            {currentBudget > 0
              ? <span>On track — your spending (~{fmt(rec.avg)}/mo) fits the {fmt(currentBudget)} budget.</span>
              : <span>No monthly budget set yet. You've averaged ~{fmt(rec.avg)}/mo.</span>}
          </div>
        ) : (
          <div>
            <div className="flex" style={{ gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 10 }}>
              {rec.action === 'lower'
                ? <TrendingDown size={16} style={{ color: 'var(--income)' }} />
                : <TrendingUp size={16} style={{ color: 'var(--expense)' }} />}
              <span>
                You've averaged <strong>{fmt(rec.avg)}/mo</strong>, {rec.action === 'lower' ? 'below' : 'above'} your{' '}
                {fmt(currentBudget)} budget. Consider {rec.action === 'lower' ? 'lowering' : 'raising'} it to{' '}
                <strong>{fmt(rec.suggested)}</strong>.
              </span>
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => onApplyBudget(rec.suggested)}>
              {rec.action === 'lower' ? <TrendingDown size={13} /> : <TrendingUp size={13} />}
              {' '}Set budget to {fmt(rec.suggested)}
            </button>
          </div>
        )}
      </div>

      {/* Monthly %-of-budget graph */}
      <SectionLabel>Monthly spend vs budget (last 12 months)</SectionLabel>
      {hasAnyBudget ? (
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: 'var(--text-tertiary)' }} axisLine={false} tickLine={false} unit="%" />
            <ReferenceLine y={100} stroke="var(--text-tertiary)" strokeDasharray="4 3" />
            <Tooltip
              cursor={{ fill: 'var(--bg-secondary)' }}
              contentStyle={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
              formatter={(v, _n, p) => [`${fmt(p.payload.actual)} of ${fmt(p.payload.budget)} (${v ?? 0}%)`, 'Spent']}
            />
            <Bar dataKey="pct" radius={[3, 3, 0, 0]} isAnimationActive={false}>
              {series.map((m, i) => (
                <Cell key={i} fill={(m.pct ?? 0) > 100 ? 'var(--expense)' : 'var(--income)'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="muted" style={{ fontSize: 13, padding: '6px 0' }}>
          Set a monthly budget to see the % graph. Total spent over the window: {fmt(total)} across {txCount} transactions.
        </div>
      )}

      {/* Outliers */}
      {outliers.length > 0 && (
        <>
          <SectionLabel><AlertTriangle size={12} style={{ verticalAlign: '-1px', marginRight: 4 }} />Unusually large</SectionLabel>
          <div>
            {outliers.slice(0, 5).map(t => (
              <ListRow
                key={t.id}
                title={t.description}
                sub={formatDate(t.date, settings)}
                value={fmt(t.amount)}
                valueClass="expense-text"
              />
            ))}
          </div>
        </>
      )}

      {/* Repetitive / recurring */}
      {repetitive.length > 0 && (
        <>
          <SectionLabel><Repeat size={12} style={{ verticalAlign: '-1px', marginRight: 4 }} />Repeating expenses</SectionLabel>
          <div>
            {repetitive.slice(0, 6).map(r => (
              <ListRow
                key={r.description}
                title={r.description}
                sub={`${r.months} months · ${r.count}×`}
                value={`${fmt(r.avgAmount)}/ea`}
              />
            ))}
          </div>
        </>
      )}

      {txCount === 0 && (
        <EmptyState title="No spending yet" sub="Log expenses in this category to see insights here." />
      )}

      {/* Edit budget (tap-users reach edit here, since edit moved to swipe) */}
      <div className="flex" style={{ gap: 8, marginTop: 18 }}>
        <button className="btn flex-1" onClick={onEdit}><Edit2 size={13} /> Edit budget</button>
      </div>
    </Modal>
  );
}
